import { Link } from "react-router-dom";
import { Phone, Check, X, ArrowRight } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import StickyCallButton from "@/components/site/StickyCallButton";
import useSEO from "@/hooks/useSEO";
import { SITE_URL } from "@/data/structuredData";

const rows = [
  { label: "Locally owned Pittsburgh business", bcw: true, national: false },
  { label: "Speak directly with the technician", bcw: true, national: false },
  { label: "Same-week scheduling", bcw: true, national: "sometimes" },
  { label: "Bilingual English/Spanish support", bcw: true, national: false },
  { label: "PA-licensed applicators (#BU15680)", bcw: true, national: true },
  { label: "Transparent pricing on first call", bcw: true, national: false },
  { label: "No aggressive upsells", bcw: true, national: false },
  { label: "Free re-treatment between visits", bcw: true, national: "extra fee" },
  { label: "24/7 emergency line answered by a human", bcw: true, national: false },
  { label: "Long-term auto-renewal contracts", bcw: false, national: true },
  { label: "Franchise call center handling your account", bcw: false, national: true },
];

const Cell = ({ value }) => {
  if (value === true) return <Check className="w-5 h-5 text-[#D9480F] mx-auto" strokeWidth={2.5} />;
  if (value === false) return <X className="w-5 h-5 text-zinc-400 mx-auto" strokeWidth={2.5} />;
  return <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{value}</span>;
};

const ComparisonPage = () => {
  useSEO({
    title: "Blue Collar World vs. Terminix & Orkin in Pittsburgh · Blue Collar World",
    description: "How Blue Collar World Pest Control compares to Terminix, Orkin, and other national chains in Pittsburgh. Local ownership, transparent pricing, no franchise runaround.",
    canonical: `${SITE_URL}/vs-competitors`,
  });

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <NavBar />

      <section className="border-b border-zinc-900 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
            [ Compare ] — Local vs. National
          </p>
          <h1 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-5xl md:text-7xl mb-6 max-w-4xl">
            Blue Collar World <br />
            <span className="text-[#0B2B40]">vs. the big brands.</span>
          </h1>
          <p className="text-zinc-700 text-base md:text-lg max-w-2xl mb-8">
            Terminix and Orkin have national brand recognition. But when a wasp nest is over your kids' swing set at 6pm on a Sunday, you need someone who picks up. Here's an honest side-by-side.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-900 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="border-2 border-black overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B2B40] text-white">
                  <th className="text-left px-4 md:px-6 py-4 font-display uppercase tracking-tight text-sm md:text-base w-1/2">
                    What matters
                  </th>
                  <th className="text-center px-2 md:px-4 py-4 font-display uppercase tracking-tight text-sm md:text-base bg-[#D9480F]">
                    Blue Collar <br className="md:hidden" />World
                  </th>
                  <th className="text-center px-2 md:px-4 py-4 font-display uppercase tracking-tight text-sm md:text-base">
                    Terminix / <br className="md:hidden" />Orkin
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F4F4F5]"}>
                    <td className="px-4 md:px-6 py-4 border-t border-zinc-300 text-sm md:text-base font-medium">
                      {r.label}
                    </td>
                    <td className="px-2 md:px-4 py-4 border-t border-zinc-300 border-l border-zinc-300 text-center">
                      <Cell value={r.bcw} />
                    </td>
                    <td className="px-2 md:px-4 py-4 border-t border-zinc-300 border-l border-zinc-300 text-center">
                      <Cell value={r.national} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-900 bg-[#0B2B40] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-4">
            The Real Difference
          </p>
          <h2 className="font-display uppercase text-3xl md:text-5xl font-semibold tracking-tighter leading-[0.9] mb-8">
            You're not a ticket number.
          </h2>
          <div className="space-y-6 text-zinc-200 text-base md:text-lg leading-relaxed">
            <p>
              With national chains, your call goes to a regional call center. Your technician changes every visit. Your contract auto-renews at higher rates. When something goes wrong, you're transferred four times.
            </p>
            <p>
              With Blue Collar World, you call{" "}
              <a href="tel:4847643392" className="text-[#F59E0B] font-bold underline">
                484-764-3392
              </a>{" "}
              and talk to us. Your technician learns your house. Your pricing is quoted once, honestly. When it matters, we show up — because we live here too.
            </p>
            <p className="font-display uppercase text-lg text-white pt-4 border-t border-[#F59E0B]/30">
              Pittsburgh built. Pittsburgh served. Simple as that.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#D9480F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-display uppercase text-4xl md:text-6xl text-white font-semibold tracking-tighter leading-[0.9] mb-6">
            Give the local guy <br /> a shot.
          </h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="tel:4847643392"
              className="inline-flex items-center gap-3 bg-black text-white px-6 py-4 font-display uppercase tracking-tight border-2 border-black"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call 484-764-3392
            </a>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-3 bg-white text-black px-6 py-4 font-display uppercase tracking-tight border-2 border-black"
            >
              Get a Quote
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default ComparisonPage;
